/**
 * Docling Client Library
 *
 * TypeScript client for interacting with the Docling PDF processing service
 * running on NVIDIA RTX 5070 (12GB VRAM, ~2.5-3.0 pages/second)
 *
 * SECURITY: Requests are proxied through the docling-proxy edge function
 * to keep API credentials server-side. Authentication is via Supabase JWT.
 *
 * Usage:
 *   import { doclingClient } from '@/services/doclingClient';
 *
 *   const result = await doclingClient.extractPDF(pdfFile, {
 *     outputFormat: 'markdown',
 *     chunkSize: 512
 *   });
 */

import type {
  DoclingOptions,
  DoclingResult,
  DoclingHealthCheck,
  DoclingBatchResult,
  DoclingConfig,
  DoclingError as DoclingErrorType
} from '@/types/docling';
import { supabase } from '@/utils/supabase';

/**
 * Custom error class for Docling service errors
 */
export class DoclingError extends Error implements DoclingErrorType {
  constructor(
    message: string,
    public statusCode?: number,
    public details?: unknown
  ) {
    super(message);
    this.name = 'DoclingError';
  }
}

/**
 * Docling PDF Processing Client
 */
export class DoclingClient {
  private baseUrl: string;
  private apiKey: string | undefined;
  private timeout: number;
  private retries: number;

  constructor(config?: Partial<DoclingConfig>) {
    this.baseUrl = config?.baseUrl || this.getDefaultBaseUrl();
    this.apiKey = this.getApiKey();
    this.timeout = config?.timeout || 600000; // 10 minutes default for large PDFs
    this.retries = config?.retries || 2;
  }

  /**
   * Get base URL - uses the docling-proxy edge function for security
   * The edge function handles API key injection server-side
   */
  private getDefaultBaseUrl(): string {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('VITE_SUPABASE_URL environment variable is required');
    }
    // Use the secure edge function proxy instead of direct Docling access
    return `${supabaseUrl}/functions/v1/docling-proxy`;
  }

  /**
   * Get API key - no longer used, edge function handles auth
   * @deprecated API key is now managed server-side in the edge function
   */
  private getApiKey(): string | undefined {
    // API key is no longer exposed to client
    // Edge function injects it server-side
    return undefined;
  }

  /**
   * Make HTTP request with retry logic
   * Uses Supabase JWT for authentication (edge function validates and proxies to Docling)
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit,
    attempt = 1
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    // Get Supabase session for JWT authentication
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
      throw new DoclingError('Authentication required for document processing');
    }

    // Use Supabase JWT for authentication (edge function validates this)
    const headers: HeadersInit = {
      Authorization: `Bearer ${session.access_token}`
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new DoclingError(
          `Docling service error: ${response.statusText}`,
          response.status,
          errorText
        );
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      // Retry on network errors or timeouts
      if (attempt < this.retries && this.isRetryableError(error)) {
        console.warn(`Docling request failed (attempt ${attempt}/${this.retries}), retrying...`);
        await this.delay(1000 * attempt); // Exponential backoff
        return this.makeRequest<T>(endpoint, options, attempt + 1);
      }

      // Re-throw DoclingError as-is
      if (error instanceof DoclingError) {
        throw error;
      }

      // Wrap other errors
      throw new DoclingError(
        error instanceof Error ? error.message : 'Unknown error',
        undefined,
        error
      );
    }
  }

  /**
   * Check if error is retryable
   */
  private isRetryableError(error: unknown): boolean {
    if (error instanceof DoclingError) {
      // Retry on 5xx server errors or network issues
      return !error.statusCode || error.statusCode >= 500;
    }

    // Retry on network errors (fetch throws TypeError)
    return error instanceof TypeError || (error as { name?: string })?.name === 'AbortError';
  }

  /**
   * Delay helper for retry backoff
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Extract text from a PDF file
   *
   * @param file - PDF file to process (File or Blob object)
   * @param options - Processing options
   * @returns Extraction result with content and metadata
   *
   * @example
   * const result = await doclingClient.extractPDF(pdfFile, {
   *   outputFormat: 'markdown',
   *   chunkSize: 512,
   *   ocr: false
   * });
   *
   * console.log(`Processed ${result.metadata.pages} pages in ${result.metadata.processing_time_seconds}s`);
   * console.log(`Speed: ${result.metadata.pages_per_second} pages/sec`);
   */
  async extractPDF(file: File | Blob, options: DoclingOptions = {}): Promise<DoclingResult> {
    const formData = new FormData();
    formData.append('file', file);

    // Add optional parameters
    if (options.outputFormat) {
      formData.append('output_format', options.outputFormat);
    }
    if (options.ocr !== undefined) {
      formData.append('ocr', String(options.ocr));
    }
    if (options.chunkSize !== undefined) {
      formData.append('chunk_size', String(options.chunkSize));
    }
    if (options.includeTables !== undefined) {
      formData.append('include_tables', String(options.includeTables));
    }
    if (options.includeImages !== undefined) {
      formData.append('include_images', String(options.includeImages));
    }
    if (options.includePageBreaks !== undefined) {
      formData.append('include_page_breaks', String(options.includePageBreaks));
    }

    return this.makeRequest<DoclingResult>('/extract', {
      method: 'POST',
      body: formData
    });
  }

  /**
   * Process multiple PDFs in batch
   *
   * @param files - Array of PDF files to process
   * @param options - Processing options (applied to all files)
   * @returns Batch result with individual results
   *
   * @example
   * const results = await doclingClient.extractBatch([file1, file2, file3], {
   *   outputFormat: 'markdown'
   * });
   *
   * results.results.forEach((result, i) => {
   *   console.log(`File ${i + 1}: ${result.metadata.pages} pages`);
   * });
   */
  async extractBatch(
    files: File[],
    options: DoclingOptions = {}
  ): Promise<DoclingBatchResult> {
    const formData = new FormData();

    // Append all files
    files.forEach(file => {
      formData.append('files', file);
    });

    // Add optional parameters
    if (options.outputFormat) {
      formData.append('output_format', options.outputFormat);
    }

    return this.makeRequest<DoclingBatchResult>('/extract-batch', {
      method: 'POST',
      body: formData
    });
  }

  /**
   * Check if Docling service is available and healthy
   *
   * @returns Health check result
   *
   * @example
   * const health = await doclingClient.healthCheck();
   *
   * if (health.status === 'healthy' && health.cuda_available) {
   *   console.log(`GPU available: ${health.gpu_name}`);
   * } else {
   *   console.warn('Docling service unavailable or running on CPU');
   * }
   */
  async healthCheck(): Promise<DoclingHealthCheck> {
    try {
      return await this.makeRequest<DoclingHealthCheck>('/health', {
        method: 'GET'
      });
    } catch (error) {
      // Return unhealthy status if service is down
      return {
        status: 'unhealthy',
        cuda_available: false,
        error: error instanceof Error ? error.message : 'Service unavailable'
      };
    }
  }

  /**
   * Check if service is available (simple boolean check)
   *
   * @returns true if service is healthy, false otherwise
   */
  async isAvailable(): Promise<boolean> {
    const health = await this.healthCheck();
    return health.status === 'healthy';
  }

  /**
   * Check if GPU is available for processing
   *
   * @returns true if CUDA/GPU is available, false otherwise
   */
  async hasGPU(): Promise<boolean> {
    const health = await this.healthCheck();
    return health.cuda_available === true;
  }
}

/**
 * Singleton instance of Docling client
 *
 * Use this for most cases:
 *   import { doclingClient } from '@/services/doclingClient';
 */
export const doclingClient = new DoclingClient();

/**
 * Create a new Docling client with custom configuration
 *
 * Use this if you need to connect to a different service URL:
 *   const client = createDoclingClient({
 *     baseUrl: 'http://192.168.1.100:8000',
 *     timeout: 300000  // 5 minutes
 *   });
 */
export function createDoclingClient(config: Partial<DoclingConfig>): DoclingClient {
  return new DoclingClient(config);
}
